/**
 * Event-Entity Mapping System
 *
 * Dynamic automation configuration based on entity relationships.
 * Add new events without rewriting entire condition/action structures.
 */

import {
  OPERATOR_TYPES_1,
  OPERATOR_TYPES_2,
  OPERATOR_TYPES_3,
  OPERATOR_TYPES_6,
} from './operators';
import { resolveAutomationConfig } from './extension-registry';

/**
 * ENTITY ATTRIBUTES
 * Define all available attributes for each entity type
 */
const BASE_ENTITY_ATTRIBUTES = {
  conversation: {
    assignee_id: {
      name: 'ASSIGNEE_NAME',
      inputType: 'search_select',
      filterOperators: OPERATOR_TYPES_3,
    },
    availability_status: {
      name: 'AVAILABILITY_STATUS',
      inputType: 'multi_select',
      filterOperators: OPERATOR_TYPES_1,
      displayName: 'Availability Status',
    },
    team_id: {
      name: 'TEAM_NAME',
      inputType: 'search_select',
      filterOperators: OPERATOR_TYPES_3,
    },
    inbox_id: {
      name: 'INBOX',
      inputType: 'multi_select',
      filterOperators: OPERATOR_TYPES_1,
    },
    status: {
      name: 'STATUS',
      inputType: 'multi_select',
      filterOperators: OPERATOR_TYPES_1,
    },
    priority: {
      name: 'PRIORITY',
      inputType: 'multi_select',
      filterOperators: OPERATOR_TYPES_1,
    },
    labels: {
      name: 'LABELS',
      inputType: 'multi_select',
      filterOperators: OPERATOR_TYPES_3,
    },
    conversation_language: {
      name: 'CONVERSATION_LANGUAGE',
      inputType: 'multi_select',
      filterOperators: OPERATOR_TYPES_1,
    },
    mail_subject: {
      name: 'MAIL_SUBJECT',
      inputType: 'plain_text',
      filterOperators: OPERATOR_TYPES_2,
    },
    referer: {
      name: 'REFERER_LINK',
      inputType: 'plain_text',
      filterOperators: OPERATOR_TYPES_2,
    },
  },

  contact: {
    email: {
      name: 'EMAIL',
      inputType: 'plain_text',
      filterOperators: OPERATOR_TYPES_2,
    },
    phone_number: {
      name: 'PHONE_NUMBER',
      inputType: 'plain_text',
      filterOperators: OPERATOR_TYPES_6,
    },
    identifier: {
      name: 'IDENTIFIER',
      displayName: 'Contact Identifier',
      inputType: 'plain_text',
      filterOperators: OPERATOR_TYPES_2,
    },
    country_code: {
      name: 'COUNTRY_NAME',
      inputType: 'search_select',
      filterOperators: OPERATOR_TYPES_1,
    },
    browser_language: {
      name: 'BROWSER_LANGUAGE',
      inputType: 'search_select',
      filterOperators: OPERATOR_TYPES_1,
    },
  },

  message: {
    message_type: {
      name: 'MESSAGE_TYPE',
      inputType: 'search_select',
      filterOperators: OPERATOR_TYPES_1,
    },
    content: {
      name: 'MESSAGE_CONTAINS',
      inputType: 'comma_separated_plain_text',
      filterOperators: OPERATOR_TYPES_2,
    },
  },
};

/**
 * EVENT CONTEXTS
 * Group events by their primary context
 */
const BASE_EVENT_CONTEXTS = {
  conversation: {
    label: 'Conversation',
    icon: '💬',
    events: [
      'conversation_created',
      'conversation_updated',
      'conversation_resolved',
      'conversation_opened',
      'assignee_changed',
      'csat_survey_response_created',
    ],
  },
  message: {
    label: 'Message',
    icon: '✉️',
    events: ['message_created'],
  },
  contact: {
    label: 'Contact',
    icon: '👤',
    events: ['contact_created', 'contact_updated'],
  },
};

/**
 * EVENT-ENTITY MAPPING
 * Maps which entities are available for each event
 */
const BASE_EVENT_ENTITIES = {
  // Conversation events
  conversation_created: ['conversation', 'contact'],
  conversation_updated: ['conversation', 'contact'],
  conversation_resolved: ['conversation', 'contact'],
  conversation_opened: ['conversation', 'contact'],
  assignee_changed: ['conversation', 'contact'],
  csat_survey_response_created: ['conversation', 'contact'],

  // Message events
  message_created: ['conversation', 'contact', 'message'],

  // Contact events
  contact_created: ['contact'],
  contact_updated: ['contact'],
};

/**
 * ACTIONS BY ENTITY
 * Maps which actions are available for each entity context
 */
const BASE_ACTIONS_BY_ENTITY = {
  conversation: [
    'delay_next_action',
    'delay_next_action_range',
    'assign_agent',
    'assign_team',
    'add_label',
    'remove_label',
    'send_message',
    'send_message_with_ai',
    'send_email_to_team',
    'send_email_transcript',
    'send_webhook_event',
    'send_attachment',
    'mute_conversation',
    'snooze_conversation',
    'open_conversation',
    'resolve_conversation',
    'move_to_kanban_stage',
    'move_to_kanban_funnel',
  ],

  contact: [
    'send_webhook_event',
    'delay_next_action',
    'delay_next_action_range',
  ],

  message: [
    'delay_next_action',
    'delay_next_action_range',
    'send_message',
    'send_message_with_ai',
    'send_webhook_event',
  ],
};

const BASE_AUTOMATION_RULE_EVENTS = [
  {
    key: 'conversation_created',
    value: 'CONVERSATION_CREATED',
    context: 'conversation',
  },
  {
    key: 'conversation_updated',
    value: 'CONVERSATION_UPDATED',
    context: 'conversation',
  },
  {
    key: 'conversation_resolved',
    value: 'CONVERSATION_RESOLVED',
    context: 'conversation',
  },
  {
    key: 'conversation_opened',
    value: 'CONVERSATION_OPENED',
    context: 'conversation',
  },
  {
    key: 'assignee_changed',
    value: 'ASSIGNEE_CHANGED',
    context: 'conversation',
  },
  {
    key: 'csat_survey_response_created',
    value: 'CSAT_SURVEY_RESPONSE_CREATED',
    context: 'conversation',
  },
  { key: 'message_created', value: 'MESSAGE_CREATED', context: 'message' },
  { key: 'contact_created', value: 'CONTACT_CREATED', context: 'contact' },
  { key: 'contact_updated', value: 'CONTACT_UPDATED', context: 'contact' },
];

const createBaseAutomationConfig = () => ({
  entityAttributes: BASE_ENTITY_ATTRIBUTES,
  eventContexts: BASE_EVENT_CONTEXTS,
  eventEntities: BASE_EVENT_ENTITIES,
  actionsByEntity: BASE_ACTIONS_BY_ENTITY,
  automationRuleEvents: BASE_AUTOMATION_RULE_EVENTS,
  customAttributeEvents: [
    'message_created',
    'conversation_created',
    'conversation_updated',
    'conversation_opened',
    'csat_survey_response_created',
  ],
});

export const getAutomationConfig = () =>
  resolveAutomationConfig(createBaseAutomationConfig());

export const getEntityAttributes = () => getAutomationConfig().entityAttributes;

export const getEventContexts = () => getAutomationConfig().eventContexts;

export const getEventEntities = () => getAutomationConfig().eventEntities;

export const getActionsByEntity = () => getAutomationConfig().actionsByEntity;

export const getCustomAttributeEvents = () =>
  getAutomationConfig().customAttributeEvents;

export const getAutomationRuleEvents = () =>
  getAutomationConfig().automationRuleEvents;

/**
 * Generate automation config for an event dynamically
 */
export function generateAutomationConfig(eventName) {
  const entities = getEventEntities()[eventName] || [];
  const entityAttributes = getEntityAttributes();
  const actionsByEntity = getActionsByEntity();

  // Build conditions from all related entities
  const conditions = [];
  entities.forEach(entity => {
    const attributes = entityAttributes[entity] || {};
    Object.keys(attributes).forEach(key => {
      conditions.push({
        key,
        ...attributes[key],
      });
    });
  });

  // Build actions from all related entities
  const actionKeys = new Set();
  entities.forEach(entity => {
    const actions = actionsByEntity[entity] || [];
    actions.forEach(action => actionKeys.add(action));
  });

  const actions = Array.from(actionKeys).map(key => ({
    key,
    name: key.toUpperCase(),
  }));

  return { conditions, actions };
}

export const getConditionKeysForEvent = eventName =>
  generateAutomationConfig(eventName).conditions.map(
    condition => condition.key
  );

export const getActionKeysForEvent = eventName =>
  generateAutomationConfig(eventName).actions.map(action => action.key);

/**
 * Generate full AUTOMATIONS object dynamically
 */
export function generateAutomationsMap() {
  const automations = {};
  const eventEntities = getEventEntities();

  Object.keys(eventEntities).forEach(eventName => {
    automations[eventName] = generateAutomationConfig(eventName);
  });

  return automations;
}

/**
 * Get events by context
 */
export function getEventsByContext(context) {
  return getAutomationRuleEvents().filter(event => event.context === context);
}
